import { api, API, tokenStore } from "./api";

/**
 * Iter-158.4.8 — Utilities sweep. The media helpers in this file are
 * plain functions (not React), so they cannot call `useApiBase()`
 * directly. Instead every helper accepts an optional `apiBase`
 * parameter that defaults to `/projects` — this keeps legacy Hair &
 * Makeup call-sites byte-identical while giving beta / multi-department
 * consumers a way to route uploads, media URLs and helper-derived
 * <img src> values through the beta workspace root.
 *
 * All React-side callers that already consume `useApiBase()` now
 * thread the resolved value into these helpers. Un-migrated callers
 * continue to fall through to the legacy `/projects` root.
 */
export async function resizeImageFile(file, maxDimension = 1600, quality = 0.85, mime = "image/jpeg") {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = url;
        });
        const ratio = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        return canvas.toDataURL(mime, quality);
    } finally {
        URL.revokeObjectURL(url);
    }
}

export async function fileToImageAssets(file) {
    const [full, thumb] = await Promise.all([
        resizeImageFile(file, 1600, 0.85),
        resizeImageFile(file, 320, 0.75),
    ]);
    return { full, thumb };
}

/**
 * Upload an original image file plus a pre-compressed display + thumbnail
 * variant to the project's media library. The original is preserved
 * byte-for-byte on the server (GridFS); the compressed variants are stored
 * alongside for fast UI rendering.
 *
 * @param {string} projectId
 * @param {File} file
 * @param {{ display?: string, thumbnail?: string, apiBase?: string }} opts
 *     `apiBase` defaults to `/projects` — pass the value returned by
 *     `useApiBase()` from the calling React component to route the
 *     upload through the correct workspace root.
 * @returns {Promise<{ id, original, display, thumb }>} the media record.
 */
export async function uploadProjectMedia(projectId, file, { display, thumbnail, apiBase = "/projects" } = {}) {
    const fd = new FormData();
    fd.append("file", file, file.name || "capture.jpg");
    if (display) fd.append("display", display);
    if (thumbnail) fd.append("thumbnail", thumbnail);
    const { data } = await api.post(`${apiBase}/${projectId}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

/**
 * Build a fully-qualified URL to a media variant that can be used inside
 * <img src> or <a download href>. JWT is appended as ?token=… because img/a
 * tags cannot send custom Authorization headers.
 *
 * NOTE: the server only accepts this token over HTTPS and validates it with
 * the same JWT secret used elsewhere. Token leakage via browser history is
 * an accepted V1 trade-off; we plan to swap to short-lived signed URLs
 * before broader rollout.
 *
 * @param {string} projectId
 * @param {string} mediaId
 * @param {"display" | "thumb" | "original"} variant
 * @param {{ download?: boolean, apiBase?: string }} opts
 */
export function mediaUrl(projectId, mediaId, variant = "display", { download = false, apiBase = "/projects" } = {}) {
    if (!projectId || !mediaId) return "";
    const token = tokenStore.get() || "";
    const params = new URLSearchParams({ variant });
    if (token) params.set("token", token);
    if (download) params.set("download", "1");
    return `${API}${apiBase}/${projectId}/media/${mediaId}/file?${params.toString()}`;
}

/**
 * If the backend already produced a workspace-correct URL on the
 * entry row (e.g. `entry.thumbnail_url` / `entry.image_url`), use it
 * verbatim with only the JWT appended. This eliminates the whole
 * "helper picked the wrong prefix" class of bug — beta rows carry
 * `/api/beta/hm-app/projects/...` URLs, legacy rows carry
 * `/api/projects/...`, and the FE stops trying to guess.
 */
function _tokenised(pathOrUrl) {
    if (!pathOrUrl) return "";
    const token = tokenStore.get() || "";
    const sep = pathOrUrl.includes("?") ? "&" : "?";
    // Backend hints are relative (`/api/...`). Prepend REACT_APP_BACKEND_URL.
    const abs = pathOrUrl.startsWith("http")
        ? pathOrUrl
        : `${API.replace(/\/api$/, "")}${pathOrUrl}`;
    return token ? `${abs}${sep}token=${encodeURIComponent(token)}` : abs;
}

/**
 * Pick the best <img src> for a state entry — prefers the backend-
 * provided workspace-correct URL, then falls back to the legacy
 * media-URL builder (which still requires apiBase), then to inline
 * base64 data.
 *
 * @param {string} projectId
 * @param {object} entry
 * @param {"display" | "thumb"} variant
 * @param {string} apiBase — defaults to `/projects`; pass useApiBase() from the caller.
 */
export function entryImageSrc(projectId, entry, variant = "display", apiBase = "/projects") {
    if (!entry) return "";
    // Iter-158.6.A1 Phase A UAT fix P0.3 — backend hint wins.
    const hint = variant === "thumb"
        ? (entry.thumbnail_url || entry.image_url)
        : (entry.image_url || entry.thumbnail_url);
    if (hint) return _tokenised(hint);
    if (entry.media_id) return mediaUrl(projectId, entry.media_id, variant, { apiBase });
    if (variant === "thumb") return entry.thumbnail_data || entry.image_data || "";
    return entry.image_data || entry.thumbnail_data || "";
}

/**
 * Same idea for a thumbnail render — chooses backend hint first,
 * then GridFS thumb URL, then inline base64.
 *
 * @param {string} projectId
 * @param {object} entry
 * @param {string} apiBase — defaults to `/projects`; pass useApiBase() from the caller.
 */
export function entryThumbSrc(projectId, entry, apiBase = "/projects") {
    if (!entry) return "";
    // Iter-158.6.A1 Phase A UAT fix P0.3 — backend hint wins.
    const hint = entry.thumbnail_url || entry.image_url;
    if (hint) return _tokenised(hint);
    if (entry.media_id) return mediaUrl(projectId, entry.media_id, "thumb", { apiBase });
    return entry.thumbnail_data || entry.image_data || "";
}

export function formatRelativeTime(iso) {
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
        return "";
    }
}

export function firstLine(text) {
    if (!text) return "";
    const line = String(text).split(/\r?\n/)[0] || "";
    return line.trim();
}
